import { toOfficialCategoryName } from '@/utils/edtechCategoryNames';

/**
 * Normalize Deal entity to canonical schema for Procurement Intelligence UI.
 * Maps current Deal fields to canonical Live Notices / Closed Awards schema.
 */

/**
 * Normalize deal for UI. Only uses account data when account.tenant_id matches deal.tenant_id
 * to prevent cross-tenant leakage. Never invents vendor defaults.
 */
export function normalizeDeal(d, account) {
  if (!d) return null;
  const acc = d.account || account;
  const accValid = acc && d && String(acc.tenant_id || '') === String(d.tenant_id || '');
  const estimatedValueRaw = d.estimated_value ?? d.estimatedValue ?? d.value;
  const estimated_value =
    estimatedValueRaw == null || estimatedValueRaw === ''
      ? null
      : (typeof estimatedValueRaw === 'number'
          ? estimatedValueRaw
          : Number(String(estimatedValueRaw).replace(/,/g, '')));

  const contractValueRaw = d.contract_value ?? d.deal_value ?? d.value;
  const contract_value =
    contractValueRaw == null || contractValueRaw === ''
      ? null
      : (typeof contractValueRaw === 'number'
          ? contractValueRaw
          : Number(String(contractValueRaw).replace(/,/g, '')));

  const canonicalId = d.deal_id ?? d.id ?? d.tender_id;
  return {
    id: canonicalId,
    deal_id: canonicalId,
    fit_deal_id: canonicalId,
    // Canonical Live fields
    institution: d.account_name || d.institution_name || d.institution || (accValid ? acc.account_name : null) || null,
    title: d.title || d.name || null,
    country: (d.country ?? (accValid ? acc.country : null)) ?? null,
    category: toOfficialCategoryName(d.category) ?? d.category ?? null,
    matched_categories: d.matched_categories || [],
    deal_type: d.deal_type || d.type || null,
    status: d.status || null,
    currency: d.currency || d.currency_code || null,
    estimated_value: estimated_value,
    value_confidence: d.value_confidence || d.estimated_value_confidence,
    incumbent_vendor: (d.incumbent_vendor ?? (accValid ? acc.incumbent_vendor : null)) ?? null,
    incumbent_confidence: d.incumbent_confidence,
    deadline: d.deadline || d.close_date || d.due_date || null,
    published_at: d.published_at || d.published_date || d.created_date || d.createdAt || null,
    last_updated_at: d.last_updated_at || d.updatedAt || null,
    source_url: d.source_url || d.full_url || d.url || d.link || null,
    cpv_codes: d.cpv_codes || [],
    has_pdf_attachment: d.has_pdf_attachment === true || d.has_pdf_attachment === 'true',
    institution_type: (d.institution_type ?? (accValid ? acc.institution_type : null)) ?? null,
    // Canonical Closed fields (match closed_deals schema: initial_term, extension, predicted_expiry, etc.)
    product_name: d.product_name || null,
    supplier: d.supplier || null,
    /* [2026-10-13] `reseller` was being DROPPED here. normalizeDeal returns a
     * whitelist, and every closed row passes through it (the `enrich` helpers in
     * Procurement.jsx replace the raw row with this object), so `reseller` was
     * undefined on every deal the client held even though the API has returned the
     * column since 2026-10-05. CompetitorIntelligence only rendered it because its
     * cell reads `d.reseller ?? deal.reseller` and fell through to the second
     * object. The new Contract Intelligence Reseller filter had no such fallback
     * and built an empty dropdown from 5,955 loaded deals. */
    reseller: d.reseller || null,
    contract_value: contract_value,
    // Benchmarking: preserve enrolment and deal_value from API (bootstrap + query must select these)
    enrolment: d.enrolment ?? d.enrollment ?? d.student_count ?? d.total_students ?? (accValid ? (acc.enrolment ?? acc.enrollment ?? acc.student_count ?? acc.total_students) : null) ?? null,
    deal_value: d.deal_value ?? d.value ?? d.amount ?? d.contract_value ?? d.total_contract_value ?? contract_value ?? null,
    deal_value_usd: d.deal_value_usd ?? d.dealValueUsd ?? null,
    neo_estimated_value: d.neo_estimated_value ?? d.neoEstimatedValue ?? null,
    initial_term_months: d.initial_term ?? d.initial_term_months ?? d.initialTerm ?? d.initialTermMonths ?? (accValid ? acc.initial_term : null) ?? null,
    extension_months: d.extension ?? d.extension_months ?? d.extensionMonths ?? (accValid ? acc.extension : null) ?? null,
    predicted_expiry_date: d.predicted_expiry ?? d.predicted_expiry_date ?? d.predictedExpiry ?? null,
    predicted_expiry_confidence: d.predicted_expiry_confidence ?? d.confidence ?? d.expiry_confidence ?? d.confidence_level ?? (accValid ? acc.predicted_expiry_confidence : null) ?? null,
    predicted_expiry_source_note: d.predicted_expiry_source_note ?? d.predictedExpirySourceNote ?? (accValid ? acc.predicted_expiry_source_note : null) ?? null,
    latest_renewal_date: d.latest_renewal_date ?? d.latest_renewal ?? d.latestRenewalDate ?? (accValid ? acc.latest_renewal_date : null) ?? null,
    procurement_method: d.procurement_method ?? d.procurementMethod ?? (accValid ? acc.procurement_method : null) ?? null,
    framework_name: d.framework_name ?? d.frameworkName ?? (accValid ? acc.framework_name : null) ?? null,
    /* [2026-10-13] Route tag from the API (api/src/services/routeFamilies.ts) for the Contract
     * Intelligence "Procurement method" column and Framework filter. This object is a WHITELIST —
     * a field not listed here is dropped from every closed row (the `reseller` lesson, ede56b0). */
    framework_id: d.framework_id ?? null,
    framework_route_type: d.framework_route_type ?? null,
    framework_route: d.framework_route ?? null,
    framework_routes: Array.isArray(d.framework_routes) ? d.framework_routes : [],
    number_of_bidders: d.number_of_bidders ?? d.numberOfBidders ?? d.num_bidders ?? (accValid ? acc.number_of_bidders : null) ?? null,
    contract_link: d.contract_link || null,
    contract_scope: d.contract_scope || null,
    attachment_urls: d.attachment_urls || [],
    pdf_attachment_urls: d.pdf_attachment_urls || [],
    notes: d.notes ?? d.deal_notes ?? d.source_notes ?? d.note ?? (accValid ? acc.notes : null) ?? null,
    contact_name: d.contact_name ?? (accValid ? acc.contact_name : null) ?? null,
    contact_title: d.contact_title ?? (accValid ? acc.contact_title : null) ?? null,
    contact_email: d.contact_email ?? (accValid ? acc.contact_email : null) ?? null,
    incumbent_sentiment: (d.incumbent_sentiment ?? d.incumbentSentiment ?? (accValid ? (acc.incumbent_sentiment ?? acc.incumbentSentiment) : null)) ?? null,
    incumbent_sentiment_rationale: (d.incumbent_sentiment_rationale ?? d.incumbentSentimentRationale ?? (accValid ? (acc.incumbent_sentiment_rationale ?? acc.incumbentSentimentRationale) : null)) ?? null,
    services_only: d.services_only ?? d.servicesOnly ?? null,
    // Product Fit (denormalized from backend)
    fit_light_score: d.fit_light_score ?? null,
    fit_light_label: d.fit_light_label ?? null,
    fit_light_recommendation: d.fit_light_recommendation ?? null,
    fit_light_confidence: d.fit_light_confidence ?? null,
    fit_light_outcome: d.fit_light_outcome ?? null,
    fit_light_updated_at: d.fit_light_updated_at ?? null,
    // Raw for compatibility
    account_name: d.account_name ?? (accValid ? acc.account_name : null),
    account: accValid ? acc : null,
    tenant_id: d.tenant_id,
  };
}

/** Resolve attachment/PDF URL - handles JSON array strings like ["https://..."] from API */
export function resolveAttachmentUrl(url) {
  if (url == null || url === '') return null;
  const s = String(url).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith('[')) {
    try {
      const parsed = JSON.parse(s);
      const first = Array.isArray(parsed) ? parsed[0] : parsed;
      if (first != null) {
        const u = typeof first === 'string' ? first.trim() : String(first).trim();
        if (u && /^https?:\/\//i.test(u)) return u;
      }
    } catch {}
  }
  return null;
}

/** Filter and join display parts, excluding empty/whitespace/placeholder to avoid "— — " */
export function joinDisplayParts(parts, separator = ' — ') {
  const filtered = (parts || [])
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter((p) => p !== '' && p !== '—');
  return filtered.length ? filtered.join(separator) : null;
}

/** Check if services_only is truthy (yes, true, 1, etc.) */
function isServicesOnlyYes(val) {
  if (val == null || val === '') return false;
  const v = String(val).trim().toLowerCase();
  return v === 'yes' || v === 'true' || v === '1' || v === 'y';
}

/** Check if status is Retired (case-insensitive) */
function isStatusRetired(val) {
  if (!val) return false;
  return String(val).trim().toLowerCase() === 'retired';
}

/** Derive decision window band from predicted_expiry_date */
export function getDecisionWindowBand(predictedExpiry) {
  if (!predictedExpiry) return { band: 'Unknown', months: null };
  try {
    const expiry = new Date(predictedExpiry);
    const now = new Date();
    const diffMs = expiry - now;
    const months = Math.round(diffMs / (30.44 * 24 * 60 * 60 * 1000));
    if (months < 0) return { band: 'Expired', months };
    if (months <= 6) return { band: '0–6 months', months };
    if (months <= 12) return { band: '6–12 months', months };
    if (months <= 24) return { band: '12–24 months', months };
    return { band: '24+ months', months };
  } catch {
    return { band: 'Unknown', months: null };
  }
}

/** Decision window band for closed deals. If services_only=yes and status=Retired, return Expired without date-based band. */
export function getDecisionWindowBandForClosedDeal(deal) {
  const raw = deal ?? {};
  const servicesOnly = raw.services_only ?? raw.servicesOnly;
  const status = raw.status ?? '';
  if (isServicesOnlyYes(servicesOnly) && isStatusRetired(status)) {
    return { band: 'Expired', months: null };
  }
  const expiry = raw.predicted_expiry_date ?? raw.predicted_expiry ?? null;
  return getDecisionWindowBand(expiry);
}

/** Get deadline horizon for Live deals (days until deadline) */
export function getDeadlineHorizon(deadline) {
  if (!deadline) return { label: 'No deadline', days: null };
  try {
    const d = new Date(deadline);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    const days = Math.ceil((d - now) / (24 * 60 * 60 * 1000));
    if (days < 0) return { label: 'Overdue', days };
    if (days <= 7) return { label: '0–7 days', days };
    if (days <= 30) return { label: '8–30 days', days };
    if (days <= 90) return { label: '31–90 days', days };
    return { label: '90+ days', days };
  } catch {
    return { label: 'No deadline', days: null };
  }
}

/** Decision window chip color (Closed) */
export function getDecisionWindowColor(band) {
  if (!band || band === 'Unknown' || band === 'Expired') return 'bg-gray-100 text-gray-700 border-gray-200';
  if (band === '0–6 months') return 'bg-red-50 text-red-700 border-red-200';
  if (band === '6–12 months') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (band === '12–24 months') return 'bg-teal-50 text-teal-700 border-teal-200';
  return 'bg-slate-50 text-slate-700 border-slate-200';
}

/** Deadline horizon chip color (Live) */
export function getDeadlineHorizonColor(label) {
  if (!label || label === 'No deadline') return 'bg-gray-100 text-gray-600 border-gray-200';
  if (label === 'Overdue' || label === '0–7 days') return 'bg-red-50 text-red-600 border-red-200';
  if (label === '8–30 days') return 'bg-amber-50 text-amber-600 border-amber-200';
  if (label === '31–90 days') return 'bg-teal-50 text-teal-600 border-teal-200';
  return 'bg-slate-50 text-slate-600 border-slate-200';
}

/** Capitalize first letter for display (e.g. neutral -> Neutral) */
export function capitalizeSentimentDisplay(sentiment) {
  const s = (sentiment || '').trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** Map varied sentiment labels to canonical negative/neutral/positive */
export function canonicalizeSentiment(sentiment) {
  const s = (sentiment || '').trim().toLowerCase();
  if (!s) return 'unknown';
  if (['negative', 'neg', 'frustrated', 'exploring alternatives', 'at risk', 'dissatisfied', 'churning'].some((x) => s.includes(x)))
    return 'negative';
  if (['positive', 'pos', 'satisfied', 'happy', 'loyal', 'committed'].some((x) => s.includes(x)))
    return 'positive';
  if (['neutral', 'neu', 'mixed', 'unknown'].some((x) => s.includes(x)))
    return s.includes('neutral') || s.includes('neu') ? 'neutral' : 'unknown';
  return 'unknown';
}

/** Incumbent sentiment pill color */
export function getIncumbentSentimentColor(sentiment) {
  const s = canonicalizeSentiment(sentiment);
  if (s === 'negative') return 'bg-red-50 text-red-700 border-red-200';
  if (s === 'neutral') return 'bg-gray-50 text-gray-700 border-gray-200';
  if (s === 'positive') return 'bg-green-50 text-green-700 border-green-200';
  return 'bg-gray-50 text-gray-500 border-gray-200';
}
